import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import {
  Alan, Anahtar, BaglantiAlan, DilAlan, DilAnahtari, GorselAlan, GorselListesi, KaydetCubugu, Secmeli, YayinDugmesi, YayinRozeti,
  useCikisUyarisi, useSiteDurum, useTaslak, type Dil, type IkiDil,
} from './ortak';

/**
 * Site › Özelleştir — tema düzenleyici (Shopify'ın "Customize" ekranının karşılığı).
 *
 *   üst çubuk   : çık · sayfa · DİL · görünüm · durum · önizlemeyi yenile · yayınla
 *   sol kenar   : sayfanın BÖLÜMLERİ, sitedeki sırasıyla → tıklayınca o bölümün ayarları
 *   sağ         : canlı önizleme (iframe). Sunucu taslağı — kaydedilmemiş hâliyle —
 *                 ayrı bir dizine derler ve belirteçli adresten sunar (POST /api/site/onizleme).
 *
 * Kaydet = taslak. Yayınla ayrı ve onaylıdır. Derleme birkaç saniye sürdüğü için
 * önizleme her tuşta değil, "Önizlemeyi yenile" ile yenilenir.
 */
type Kart = { anahtar: string; baslik: IkiDil; aciklama: IkiDil; gorsel: string; baglanti: string; odak: 'center' | 'top' | 'bottom' };
type Baslikli = { baslik: IkiDil; metin: IkiDil };
type Anasayfa = {
  seo: { baslik: IkiDil; aciklama: IkiDil };
  hero: { ust: IkiDil; kelimeler: IkiDil[]; alt: IkiDil; dugmeler: { metin: IkiDil; baglanti: string }[]; gorseller: string[] };
  duyuru: { acik: boolean; metin: IkiDil; baglanti: string };
  vitrin: Baslikli & { kartlar: Kart[] };
  bant: Baslikli & { karolar: Kart[] };
  uygulamalar: Baslikli; projeler: Baslikli;
};

/** Düzenlenebilen sayfalar. Şimdilik yalnız ana sayfa; yeni sayfa buraya eklenir. */
const SAYFALAR = [{ id: 'anasayfa', ad: 'Ana sayfa', yol: '' }] as const;

type BolumId = 'duyuru' | 'hero' | 'vitrin' | 'bant' | 'uygulamalar' | 'projeler' | 'seo';
const dilde = (v: IkiDil, dil: Dil) => v[dil] || v.tr;
/**
 * Ana sayfanın bölümleri — sitedeki sırayla (Base.astro + pages/index.astro).
 * `cipa`: önizlemede o bölüme kaydırmak için sitedeki sabit kimlik.
 */
const BOLUMLER: { id: BolumId; ad: string; yer: string; cipa: string; ozet: (f: Anasayfa, dil: Dil) => string }[] = [
  { id: 'duyuru', ad: 'Duyuru bandı', yer: 'Menünün hemen altı, her sayfada', cipa: 'top', ozet: (f, d) => (f.duyuru.acik ? `Açık · ${dilde(f.duyuru.metin, d)}` : 'Kapalı') },
  { id: 'hero', ad: 'Hero', yer: 'Sayfanın en üstü', cipa: 'top', ozet: (f, d) => f.hero.kelimeler.map((k) => dilde(k, d)).filter(Boolean).join(' ') },
  { id: 'vitrin', ad: 'Vitrin', yer: 'Üç büyük kart', cipa: 'vitrin-title', ozet: (f, d) => dilde(f.vitrin.baslik, d) || `${f.vitrin.kartlar.length} kart` },
  { id: 'bant', ad: 'Üretim hatları', yer: 'Karo bandı', cipa: 'bant-title', ozet: (f, d) => dilde(f.bant.baslik, d) || `${f.bant.karolar.length} karo` },
  { id: 'uygulamalar', ad: 'Uygulamalar başlığı', yer: 'Fotoğraf kolajının üstü', cipa: 'uygulama-title', ozet: (f, d) => dilde(f.uygulamalar.baslik, d) },
  { id: 'projeler', ad: 'Projeler başlığı', yer: 'Referans listesinin üstü', cipa: 'projects-title', ozet: (f, d) => dilde(f.projeler.baslik, d) },
  { id: 'seo', ad: 'Arama motoru', yer: 'Google sonucu ve sekme başlığı', cipa: 'top', ozet: (f, d) => dilde(f.seo.baslik, d) },
];

/** Tekrar eden öğe (kart, karo, düğme): başlığına tıklayınca açılır. */
function Blok({ baslik, alt, acik, ac, children }: { baslik: string; alt?: string; acik: boolean; ac: () => void; children: React.ReactNode }) {
  return (
    <div className={`ccd-tema-blok${acik ? ' ccd-is-active' : ''}`}>
      <button type="button" className="ccd-tema-blok-bas" aria-expanded={acik} onClick={ac}>
        <span className="ccd-tema-blok-ok" aria-hidden="true">{acik ? '▾' : '▸'}</span>
        <span className="ccd-truncate">{baslik || 'Adsız'}</span>
        {alt && <span className="ccd-tema-blok-alt ccd-truncate">{alt}</span>}
      </button>
      {acik && <div className="ccd-tema-blok-govde ccd-stack">{children}</div>}
    </div>
  );
}

function Grup({ baslik, children }: { baslik: string; children: React.ReactNode }) {
  return (
    <div className="ccd-tema-grup">
      <div className="ccd-tema-grup-baslik">{baslik}</div>
      <div className="ccd-stack">{children}</div>
    </div>
  );
}

/** Google sonucunun küçük önizlemesi. */
export function AramaOnizleme({ baslik, adres, aciklama }: { baslik: string; adres: string; aciklama: string }) {
  return (
    <div className="ccd-site-google">
      <div className="ccd-site-google-adres ccd-truncate">{adres}</div>
      <div className="ccd-site-google-baslik">{baslik || 'Başlık'}</div>
      <div className="ccd-site-google-aciklama">{aciklama || 'Açıklama girilmedi.'}</div>
    </div>
  );
}

type Onizleme = { belirtec: string; yol: string };

export default function SiteOzellestir() {
  const navigate = useNavigate();
  const { durum, yenile } = useSiteDurum();
  const { f, setF, yukle, kirli, vazgec } = useTaslak<Anasayfa>();
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [dil, setDil] = useState<Dil>('tr');
  const [gorunum, setGorunum] = useState<'masaustu' | 'telefon'>('masaustu');
  const [sayfa] = useState<(typeof SAYFALAR)[number]['id']>('anasayfa');
  const [bolum, setBolum] = useState<BolumId | null>(null);
  const [blok, setBlok] = useState<string | null>(null);
  /** Dar ekranda kenar çubuğu ile önizleme sekmelidir. */
  const [sekme, setSekme] = useState<'duzenle' | 'onizle'>('duzenle');

  const [onizleme, setOnizleme] = useState<Onizleme | null>(null);
  const [derleniyor, setDerleniyor] = useState(false);
  const [onizlemeHata, setOnizlemeHata] = useState<string | null>(null);
  /** Önizlemedeki içerik (JSON) — formla farklıysa "yenile" düğmesinde nokta yanar. */
  const [onizlenen, setOnizlenen] = useState<string | null>(null);
  const belirtecRef = useRef<string | null>(null);

  const cikabilir = useCikisUyarisi(kirli);

  useEffect(() => {
    api<{ veri: Anasayfa }>('/api/site/icerik/anasayfa').then((r) => yukle(r.veri)).catch((e) => setHata((e as Error).message));
  }, [yukle]);

  const hatKurulu = durum?.hatKurulu ?? null;
  const derle = useCallback(async (taslak: Anasayfa) => {
    setDerleniyor(true); setOnizlemeHata(null);
    const govde = JSON.stringify(taslak);
    try {
      const r = await api<Onizleme>('/api/site/onizleme', { method: 'POST', body: JSON.stringify({ taslak: { anasayfa: taslak } }) });
      belirtecRef.current = r.belirtec;
      setOnizleme({ belirtec: r.belirtec, yol: r.yol }); setOnizlenen(govde);
    } catch (e) {
      setOnizlemeHata(e instanceof ApiError && e.status === 409 ? e.message : e instanceof Error ? e.message : 'Önizleme derlenemedi');
    } finally { setDerleniyor(false); }
  }, []);

  // İlk açılışta bir kez derlenir (yayın hattı kuruluysa). Sonrası "Önizlemeyi yenile" ile.
  const ilkDerleme = useRef(false);
  useEffect(() => {
    if (ilkDerleme.current || !f || hatKurulu !== true) return;
    ilkDerleme.current = true;
    void derle(f);
  }, [f, hatKurulu, derle]);

  // Çıkarken sunucudaki önizleme dizini bırakılır.
  useEffect(() => () => {
    if (belirtecRef.current) void api(`/api/site/onizleme/${belirtecRef.current}`, { method: 'DELETE' }).catch(() => undefined);
  }, []);

  if (!f) {
    return (
      <div className="ccd"><div className="ccd-tema">
        <div className="ccd-tema-ust"><button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => navigate('/site')}>← Çık</button></div>
        <div className="ccd-tema-bos">{hata ? <div className="ccd-site-not ccd-site-not-hata" role="alert">{hata}</div> : <div className="ccd-inline-note">Yükleniyor…</div>}</div>
      </div></div>
    );
  }

  const al = <K extends keyof Anasayfa>(k: K, v: Anasayfa[K]) => setF({ ...f, [k]: v });
  const hero = (parca: Partial<Anasayfa['hero']>) => al('hero', { ...f.hero, ...parca });
  const bayat = onizlenen !== null && onizlenen !== JSON.stringify(f);

  async function kaydet() {
    setHata(null); setMesgul(true);
    try {
      const r = await api<{ veri: Anasayfa }>('/api/site/icerik/anasayfa', { method: 'PUT', body: JSON.stringify(f) });
      // Sunucu metinleri kırpar; önizleme "bayat" görünmesin diye aynı içerik sayılır.
      if (onizlenen === JSON.stringify(f)) setOnizlenen(JSON.stringify(r.veri));
      yukle(r.veri);
      await yenile();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
    finally { setMesgul(false); }
  }

  function bolumAc(id: BolumId | null) {
    setBolum(id); setBlok(null);
  }

  const secili = BOLUMLER.find((b) => b.id === bolum) ?? null;
  const cipa = secili?.cipa ?? 'top';
  const kartBlok = (k: Kart, i: number, tur: 'vitrin' | 'bant') => {
    const degis = (y: Kart) => (tur === 'vitrin'
      ? al('vitrin', { ...f.vitrin, kartlar: f.vitrin.kartlar.map((x, n) => (n === i ? y : x)) })
      : al('bant', { ...f.bant, karolar: f.bant.karolar.map((x, n) => (n === i ? y : x)) }));
    return (
      <Blok key={k.anahtar} baslik={dilde(k.baslik, dil)} alt={dilde(k.aciklama, dil)} acik={blok === k.anahtar} ac={() => setBlok(blok === k.anahtar ? null : k.anahtar)}>
        <DilAlan id={`t-${k.anahtar}-b`} etiket="Başlık" dil={dil} deger={k.baslik} degis={(v) => degis({ ...k, baslik: v })} azami={60} />
        <DilAlan id={`t-${k.anahtar}-a`} etiket="Alt satır" dil={dil} deger={k.aciklama} degis={(v) => degis({ ...k, aciklama: v })} azami={140} />
        <GorselAlan etiket="Görsel" deger={k.gorsel} degis={(v) => degis({ ...k, gorsel: v })} zorunlu={tur === 'vitrin'}
                    ipucu={tur === 'bant' && !k.gorsel ? 'Görselsiz karo lacivert zeminde yalnız yazıyla basılır.' : undefined} />
        <BaglantiAlan id={`t-${k.anahtar}-l`} deger={k.baglanti} degis={(v) => degis({ ...k, baglanti: v })} />
        {tur === 'bant' && (
          <details className="ccd-tema-ileri">
            <summary>Gelişmiş</summary>
            <Alan etiket="Görselin görünen kısmı">
              <Secmeli etiket="Görselin görünen kısmı" tam secenekler={[['top', 'Üst'], ['center', 'Orta'], ['bottom', 'Alt']] as const}
                       deger={k.odak} degis={(v) => degis({ ...k, odak: v })} />
            </Alan>
          </details>
        )}
      </Blok>
    );
  };

  const baslikMetin = (anahtar: 'vitrin' | 'bant' | 'uygulamalar' | 'projeler', metinIpucu?: string) => {
    const b = f[anahtar] as Baslikli;
    const degis = (parca: Partial<Baslikli>) => setF({ ...f, [anahtar]: { ...f[anahtar], ...parca } });
    return (
      <Grup baslik="Bölüm başlığı">
        <DilAlan id={`t-${anahtar}-b`} etiket="Başlık" dil={dil} deger={b.baslik} degis={(v) => degis({ baslik: v })} azami={120} />
        <DilAlan id={`t-${anahtar}-m`} etiket="Metin" dil={dil} deger={b.metin} degis={(v) => degis({ metin: v })} azami={300} cokSatir ipucu={metinIpucu} />
      </Grup>
    );
  };

  const ayarlar = () => {
    switch (bolum) {
      case 'duyuru': return (
        <>
          <Grup baslik="Görünürlük">
            <Anahtar acik={f.duyuru.acik} degis={(v) => al('duyuru', { ...f.duyuru, acik: v })} etiket={f.duyuru.acik ? 'Bant açık' : 'Bant kapalı'} />
          </Grup>
          <Grup baslik="İçerik">
            <DilAlan id="t-duyuru" etiket="Metin" dil={dil} deger={f.duyuru.metin} degis={(v) => al('duyuru', { ...f.duyuru, metin: v })} azami={160} sayac
                     ipucu="Tek satır: kampanya, tatil duyurusu, fuar." />
            <BaglantiAlan id="t-duyuru-l" deger={f.duyuru.baglanti} degis={(v) => al('duyuru', { ...f.duyuru, baglanti: v })} ipucu="Doluysa bant tıklanabilir olur." />
          </Grup>
        </>
      );
      case 'hero': return (
        <>
          <Grup baslik="Yazılar">
            <DilAlan id="t-ust" etiket="Üst yazı" dil={dil} deger={f.hero.ust} degis={(v) => hero({ ust: v })} azami={200} cokSatir satir={2} />
            <div className="ccd-field">
              <span className="ccd-field-label">Başlık<span className="ccd-site-dil-cip">{dil.toUpperCase()}</span></span>
              <div className="ccd-stack-sm">
                {f.hero.kelimeler.map((k, i) => (
                  <input key={i} className="ccd-input" maxLength={24} aria-label={`Başlık ${i + 1}. satır`} lang={dil} value={k[dil]}
                         placeholder={dil === 'en' ? k.tr : `${i + 1}. satır`}
                         onChange={(e) => hero({ kelimeler: f.hero.kelimeler.map((x, n) => (n === i ? { ...x, [dil]: e.target.value } : x)) })} />
                ))}
              </div>
              <div className="ccd-field-hint">Dört satır; sitede büyük harfle basılır.</div>
            </div>
            <DilAlan id="t-alt" etiket="Alt satır" dil={dil} deger={f.hero.alt} degis={(v) => hero({ alt: v })} azami={120} ipucu="1994'ün yanındaki yazı." />
          </Grup>
          <Grup baslik="Düğmeler">
            {f.hero.dugmeler.map((d, i) => (
              <Blok key={i} baslik={dilde(d.metin, dil) || `${i + 1}. düğme`} alt={d.baglanti} acik={blok === `d${i}`} ac={() => setBlok(blok === `d${i}` ? null : `d${i}`)}>
                <DilAlan id={`t-d${i}`} etiket="Metin" dil={dil} deger={d.metin} azami={30}
                         degis={(v) => hero({ dugmeler: f.hero.dugmeler.map((x, n) => (n === i ? { ...x, metin: v } : x)) })} />
                <BaglantiAlan id={`t-d${i}-l`} deger={d.baglanti}
                              degis={(v) => hero({ dugmeler: f.hero.dugmeler.map((x, n) => (n === i ? { ...x, baglanti: v } : x)) })} />
              </Blok>
            ))}
          </Grup>
          <Grup baslik="Görseller">
            <GorselListesi etiket="Yedek kareler" deger={f.hero.gorseller} degis={(v) => hero({ gorseller: v })} azami={4} bos="Seçilmedi; sitedeki bugünkü dört kare kullanılır." />
            <div className="ccd-field-hint">Sitede Instagram'ın son dört paylaşımı görünür; bu kareler akış okunamazsa devreye girer.</div>
          </Grup>
        </>
      );
      case 'vitrin': return (
        <>
          {baslikMetin('vitrin')}
          <Grup baslik={`Kartlar (${f.vitrin.kartlar.length})`}>
            {f.vitrin.kartlar.length === 0 && <div className="ccd-field-hint">Kartlar panele alınmadı; sitede bugünkü kartlar kalır.</div>}
            {f.vitrin.kartlar.map((k, i) => kartBlok(k, i, 'vitrin'))}
          </Grup>
        </>
      );
      case 'bant': return (
        <>
          {baslikMetin('bant')}
          <Grup baslik={`Karolar (${f.bant.karolar.length})`}>
            {f.bant.karolar.length === 0 && <div className="ccd-field-hint">Karolar panele alınmadı; sitede bugünkü karolar kalır.</div>}
            {f.bant.karolar.map((k, i) => kartBlok(k, i, 'bant'))}
          </Grup>
        </>
      );
      case 'uygulamalar': return baslikMetin('uygulamalar');
      case 'projeler': return baslikMetin('projeler', 'Projeler sayfasındaki listenin başında da görünür.');
      case 'seo': return (
        <>
          <Grup baslik="Google'da görünüm">
            <AramaOnizleme baslik={dilde(f.seo.baslik, dil)} aciklama={dilde(f.seo.aciklama, dil)}
                           adres={`${(durum?.siteAdresi ?? 'site').replace(/^https?:\/\//, '').replace(/\/$/, '')}${dil === 'en' ? ' › en' : ''}`} />
          </Grup>
          <Grup baslik="Ana sayfa">
            <DilAlan id="t-seo-b" etiket="Sayfa başlığı" dil={dil} deger={f.seo.baslik} degis={(v) => al('seo', { ...f.seo, baslik: v })} azami={120} sayac />
            <DilAlan id="t-seo-a" etiket="Açıklama" dil={dil} deger={f.seo.aciklama} degis={(v) => al('seo', { ...f.seo, aciklama: v })} azami={300} cokSatir sayac />
          </Grup>
        </>
      );
      default: return null;
    }
  };

  const adres = onizleme ? `${onizleme.yol}${SAYFALAR.find((s) => s.id === sayfa)?.yol ?? ''}${dil === 'en' ? 'en/' : ''}#${cipa}` : null;

  return (
    <div className="ccd">
      <div className="ccd-tema" data-tema-sekme={sekme}>
        <KaydetCubugu acik={kirli} mesgul={mesgul} hata={hata} kaydet={() => void kaydet()} vazgec={() => { setHata(null); vazgec(); }} />

        <header className="ccd-tema-ust">
          <div className="ccd-tema-ust-grup">
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => { if (cikabilir()) navigate('/site'); }}>← Çık</button>
            <select className="ccd-select ccd-tema-sayfa" aria-label="Düzenlenen sayfa" value={sayfa} onChange={() => undefined}>
              {SAYFALAR.map((s) => <option key={s.id} value={s.id}>{s.ad}</option>)}
            </select>
          </div>
          <div className="ccd-tema-ust-grup ccd-tema-ust-orta">
            <DilAnahtari dil={dil} degis={setDil} />
            <span className="ccd-tema-genis">
              <Secmeli etiket="Önizleme genişliği" secenekler={[['masaustu', 'Masaüstü'], ['telefon', 'Telefon']] as const} deger={gorunum} degis={setGorunum} />
            </span>
          </div>
          <div className="ccd-tema-ust-grup">
            <span className="ccd-tema-genis"><YayinRozeti durum={durum} /></span>
            <button type="button" className="ccd-btn ccd-btn-sm" disabled={!kirli || mesgul} onClick={() => void kaydet()}>{mesgul ? 'Kaydediliyor…' : 'Kaydet'}</button>
            <YayinDugmesi durum={durum} yenile={() => void yenile()} kucuk engel={kirli ? 'Önce değişiklikleri kaydedin' : null} />
          </div>
        </header>

        <div className="ccd-tema-sekmeler" role="tablist">
          {([['duzenle', 'Düzenle'], ['onizle', 'Önizle']] as const).map(([id, ad]) => (
            <button key={id} type="button" role="tab" aria-selected={sekme === id} className={`ccd-tab${sekme === id ? ' ccd-is-active' : ''}`} onClick={() => setSekme(id)}>
              {ad}{id === 'onizle' && bayat && <span className="ccd-tema-nokta" aria-label="önizleme güncel değil" />}
            </button>
          ))}
        </div>

        <div className="ccd-tema-govde">
          <aside className="ccd-tema-kenar ccd-scroll" aria-label={secili ? `${secili.ad} ayarları` : 'Bölümler'}>
            {!secili ? (
              <>
                <div className="ccd-tema-kenar-bas">
                  <div className="ccd-tema-kenar-baslik">{SAYFALAR.find((s) => s.id === sayfa)?.ad}</div>
                  <div className="ccd-meta">Bölümler sitedeki sırasıyla.</div>
                </div>
                <nav className="ccd-tema-bolumler">
                  {BOLUMLER.map((b) => (
                    <button key={b.id} type="button" className="ccd-tema-bolum" data-tema-bolum={b.id} onClick={() => bolumAc(b.id)}>
                      <span className="ccd-tema-bolum-metin">
                        <span className="ccd-tema-bolum-ad">{b.ad}</span>
                        <span className="ccd-tema-bolum-ozet ccd-truncate">{b.ozet(f, dil) || b.yer}</span>
                      </span>
                      <span className="ccd-tema-bolum-ok" aria-hidden="true">›</span>
                    </button>
                  ))}
                </nav>
              </>
            ) : (
              <>
                <div className="ccd-tema-kenar-bas ccd-tema-kenar-bas-yapisik">
                  <button type="button" className="ccd-tema-geri" onClick={() => bolumAc(null)}>← Bölümler</button>
                  <div className="ccd-tema-kenar-baslik">{secili.ad}</div>
                  <div className="ccd-meta">{secili.yer}</div>
                </div>
                <div className="ccd-tema-ayarlar">{ayarlar()}</div>
              </>
            )}
          </aside>

          <section className="ccd-tema-onizleme" aria-label="Canlı önizleme">
            {hatKurulu === false ? (
              <div className="ccd-tema-onizleme-bos">
                <div className="ccd-site-kart-baslik">Önizleme bu sunucuda kapalı</div>
                <div className="ccd-meta">Site yayın hattı kurulmadığı için taslak derlenemiyor. Düzenleyip kaydedebilirsiniz.</div>
                {durum?.siteAdresi && <a className="ccd-btn ccd-btn-sm" href={durum.siteAdresi} target="_blank" rel="noreferrer">Canlı siteyi aç</a>}
              </div>
            ) : (
              <>
                <div className="ccd-tema-onizleme-ust">
                  <span className="ccd-meta ccd-truncate">
                    {derleniyor ? 'Önizleme derleniyor…' : onizlemeHata ? 'Önizleme derlenemedi' : bayat ? 'Önizleme güncel değil' : onizleme ? 'Taslağın önizlemesi' : 'Önizleme hazırlanıyor…'}
                  </span>
                  <button type="button" className="ccd-btn ccd-btn-sm" data-tema-yenile disabled={derleniyor || hatKurulu === null} onClick={() => void derle(f)}>
                    Önizlemeyi yenile{bayat && !derleniyor && <span className="ccd-tema-nokta" aria-label="değişiklik var" />}
                  </button>
                </div>
                {derleniyor && <div className="ccd-tema-ilerleme" role="progressbar" aria-label="Önizleme derleniyor" />}
                {onizlemeHata && <div className="ccd-site-not ccd-site-not-hata ccd-tema-onizleme-hata" role="alert">{onizlemeHata}</div>}
                <div className={`ccd-tema-cerceve${gorunum === 'telefon' ? ' ccd-tema-cerceve-telefon' : ''}`}>
                  {adres
                    // Kum havuzu: önizleme panelle aynı adresten gelir ama panelin oturumuna erişemez.
                    ? <iframe key={onizleme!.belirtec} title="Site önizlemesi" src={adres} sandbox="allow-scripts" referrerPolicy="no-referrer" />
                    : !onizlemeHata && <div className="ccd-tema-onizleme-bos"><div className="ccd-inline-note">Önizleme derleniyor…</div></div>}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
